import { useState } from "react";
import { ActionModal, Sheet } from "@cntrl/ui";
import type { components } from "@cntrl/api-client";
import { useMe } from "../../../app/hooks/useSession.js";
import {
  advanceActions,
  approvalActions,
  formatDropDate,
  statusLabel,
  statusTone,
  useDeleteTask,
  useTransitionTask,
} from "../../../app/hooks/useTasks.js";
import { PersonAvatar } from "../../shared/PersonAvatar.js";
import type { TaskPeople } from "../../tasks/taskPeople.js";
import styles from "./MobileHome.module.scss";

type TaskSummary = components["schemas"]["TaskSummary"];

/**
 * The mobile To-Do detail sheet (ART-179) — the phone's answer to the desktop task drawer. A row on the
 * To-Do card opens this bottom {@link Sheet}: the release/phase eyebrow, the title, the due date, the
 * assignee's face + name, and an editable STATUS control. Close dismisses.
 *
 * <b>The status control is the real transition graph, not a free status picker.</b> Task status is a
 * server-validated state machine (`advanceActions` / `approvalActions`), so the pills are the CURRENT status
 * (shown selected) plus exactly the moves the server permits from it — a flat "set any status" picker would
 * offer transitions the server 422s. Approval moves (Approve / Request revisions from `needs_review`) show
 * only to the task's initiator, matching the drawer. The move rides {@link useTransitionTask}, which is
 * optimistic — the parent card re-renders with the new status, so the sheet reflects it in place.
 *
 * <b>The creator can delete it here (ART-320)</b>, as on the desktop drawer: the same initiator-only rule and the
 * same `DELETE` route (which refuses anyone else), behind an "are you sure" — a delete cannot be undone.
 */
export function TodoDetailSheet({
  task,
  people,
  onClose,
}: {
  task: TaskSummary;
  people: TaskPeople;
  onClose: () => void;
}) {
  const me = useMe();
  const transition = useTransitionTask();
  const del = useDeleteTask();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const status = task.status ?? "open";
  const isInitiator = !!task.initiatorPersonId && task.initiatorPersonId === me.data?.person?.personId;

  // The reachable states from here, as target-status pills (the mock's noun pills), deduped and never
  // re-offering the current one. Approval moves are initiator-only.
  const moves = [...advanceActions(status), ...(isInitiator ? approvalActions(status) : [])];
  const targets = [...new Set(moves.map((m) => m.toStatus))].filter((s) => s !== status);

  const assignee = task.assignees?.[0]?.personId ?? null;
  const assigneeName = people.nameOf(assignee, "Unassigned");

  // The eyebrow: the release this belongs to, then its phase — the two facts that place a task. Uppercased
  // by the eyebrow style; absent halves drop rather than leaving a stray separator.
  const eyebrow = [task.releaseTitle, task.phase].filter(Boolean).join(" · ") || "Task";

  const move = (toStatus: string) => {
    if (transition.isPending || task.version == null || task.taskId == null) return;
    transition.mutate({ taskId: task.taskId, toStatus, version: task.version });
  };

  return (
    <Sheet title={task.title ?? "Task"} onClose={onClose} closeLabel="Close task">
      <div className={styles.sheet}>
        <p className={styles.sheetEyebrow}>{eyebrow}</p>
        <h2 className={styles.sheetTitle}>{task.title ?? "Untitled task"}</h2>
        {task.dueDate && <p className={styles.sheetDue}>Due {formatDropDate(task.dueDate)}</p>}

        <div className={styles.sheetField}>
          <p className={styles.sheetLabel}>Assignee</p>
          <div className={styles.sheetAssignee}>
            <PersonAvatar
              personId={assignee}
              avatarMediaId={people.person(assignee)?.avatarMediaId}
              initials={people.initialsOf(assignee, assigneeName)}
              size="sm"
            />
            <span className={styles.sheetAssigneeName}>{assigneeName}</span>
          </div>
        </div>

        <div className={styles.sheetField}>
          <p className={styles.sheetLabel}>Status</p>
          <div className={styles.statusRow}>
            <span className={styles.statusPill} data-tone={statusTone(status)} data-selected aria-current="true">
              <span className={styles.statusDot} data-tone={statusTone(status)} aria-hidden="true" />
              {statusLabel(status)}
            </span>
            {targets.map((toStatus) => (
              <button
                key={toStatus}
                type="button"
                className={styles.statusPill}
                data-tone={statusTone(toStatus)}
                onClick={() => move(toStatus)}
                disabled={transition.isPending}
              >
                <span className={styles.statusDot} data-tone={statusTone(toStatus)} aria-hidden="true" />
                {statusLabel(toStatus)}
              </button>
            ))}
          </div>
          {transition.isError && (
            <p className={styles.sheetError}>Couldn't update the status. Please try again.</p>
          )}
        </div>

        <button type="button" className={styles.sheetClose} onClick={onClose}>
          Close
        </button>
        {isInitiator && task.taskId && (
          <button type="button" className={styles.sheetDelete} onClick={() => setConfirmDelete(true)}>
            Delete task
          </button>
        )}
      </div>
      {confirmDelete && task.taskId && (
        <ActionModal
          title="Delete task?"
          confirmLabel="Delete"
          confirmTone="danger"
          loading={del.isPending}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() =>
            del.mutate(task.taskId as string, {
              onSuccess: () => {
                setConfirmDelete(false);
                onClose();
              },
            })
          }
        >
          <p>{task.title || "This task"} will be deleted. This can't be undone.</p>
        </ActionModal>
      )}
    </Sheet>
  );
}
